// ── KostenartenPage ───────────────────────────────────────────────────────────
// Admin page for managing dim.Kostenart (Phase 3 of the Kostenart restructure).
// Two sections: Kosten (kind='cost') and Erlöse (kind='revenue').
// Within each section: root rows with their children indented beneath.
// Writes: inline name edit + Toggle (deactivate/activate) + ▲▼ sort_order swap.
// Create: 'Neue Kostenart' button opens a modal.
// No delete anywhere — deactivate only.

import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, PageHeader } from '../../../shared/ui'
import {
  useKostenarten,
  useCreateKostenart,
  useUpdateKostenart,
  type Kostenart,
  type CreateKostenartPayload,
} from './api/kostenartenApi'
import { KostenartRow } from './components/KostenartRow'
import { CreateKostenartModal } from './components/CreateKostenartModal'
import { ToggleActiveModal } from './components/ToggleActiveModal'

// ── Sort helpers ──────────────────────────────────────────────────────────────

/** Returns roots (parentId=null) sorted by sortOrder, then children of each. */
function buildTree(rows: Kostenart[], kind: 'cost' | 'revenue'): Kostenart[][] {
  const ofKind = rows.filter(r => r.kind === kind)
  const roots = ofKind
    .filter(r => r.parentId === null)
    .sort((a, b) => a.sortOrder - b.sortOrder)
  return roots.map(root => {
    const children = ofKind
      .filter(r => r.parentId === root.id)
      .sort((a, b) => a.sortOrder - b.sortOrder)
    return [root, ...children]
  })
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function KostenartenPage() {
  const { t } = useTranslation('kostenarten')
  const { data: all = [], isLoading, isError } = useKostenarten()
  const createMutation = useCreateKostenart()
  const updateMutation = useUpdateKostenart()

  const [showInactive, setShowInactive] = useState(false)
  const [createOpen, setCreateOpen] = useState(false)
  const [confirmRow, setConfirmRow] = useState<Kostenart | null>(null)
  const [confirmOpen, setConfirmOpen] = useState(false)
  // Accordion: root ids whose children are shown. Collapsed by default.
  const [expandedRoots, setExpandedRoots] = useState<Set<number>>(new Set())
  function toggleRoot(id: number) {
    setExpandedRoots(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const isMutating = updateMutation.isPending || createMutation.isPending

  // Filter out inactive rows when showInactive=false
  const visible = showInactive ? all : all.filter(r => r.active)

  function handleNameSave(row: Kostenart, newName: string) {
    updateMutation.mutate({ id: row.id, payload: { name: newName } })
  }

  function handleToggleActiveClick(row: Kostenart) {
    setConfirmRow(row)
    setConfirmOpen(true)
  }

  function handleConfirmToggle() {
    if (!confirmRow) return
    updateMutation.mutate(
      { id: confirmRow.id, payload: { active: !confirmRow.active } },
      { onSuccess: () => setConfirmOpen(false) },
    )
  }

  /**
   * Swap sortOrder between two adjacent siblings.
   * We send PATCH for both rows; invalidation via the mutation's onSuccess
   * takes care of refetching the list.
   */
  function handleMove(row: Kostenart, siblings: Kostenart[], direction: 'up' | 'down') {
    const idx = siblings.findIndex(s => s.id === row.id)
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1
    if (swapIdx < 0 || swapIdx >= siblings.length) return
    const other = siblings[swapIdx]
    // Swap sort_order values
    updateMutation.mutate({ id: row.id,    payload: { sortOrder: other.sortOrder } })
    updateMutation.mutate({ id: other.id,  payload: { sortOrder: row.sortOrder } })
  }

  function handleCreate(payload: CreateKostenartPayload) {
    createMutation.mutate(payload, { onSuccess: () => setCreateOpen(false) })
  }

  // Roots for the create-modal parent dropdown
  const roots = all.filter(r => r.parentId === null)

  // ── Render a kind section ─────────────────────────────────────────────────

  function renderSection(kind: 'cost' | 'revenue') {
    const groups = buildTree(visible, kind)

    return (
      <section key={kind} className="mb-8">
        {/* Section header */}
        <div className="flex items-center gap-2 mb-3">
          <span
            className={`material-symbols-outlined !text-lg
              ${kind === 'cost' ? 'text-rose-500' : 'text-emerald-600'}`}
          >
            {kind === 'cost' ? 'trending_down' : 'trending_up'}
          </span>
          <h2 className="text-sm font-semibold uppercase tracking-wider text-on-surface-variant">
            {t(`section.${kind}`)}
          </h2>
          <span className="text-xs text-on-surface-variant/60 font-mono">
            ({visible.filter(r => r.kind === kind).length})
          </span>
        </div>

        {/* Rows */}
        {groups.length === 0 ? (
          <p className="text-sm text-on-surface-variant px-3 py-2 italic">{t('empty')}</p>
        ) : (
          <div className="rounded-2xl border border-outline-variant/30 bg-surface-container-lowest divide-y divide-outline-variant/20">
            {groups.map((group) => {
              const [root, ...children] = group

              // Siblings at root level (for ▲▼ bounds) — only roots, not children
              const rootSiblings = visible
                .filter(r => r.kind === kind && r.parentId === null)
                .sort((a, b) => a.sortOrder - b.sortOrder)
              const rootIdx = rootSiblings.findIndex(r => r.id === root.id)

              return (
                <div key={root.id} className="px-2 py-1">
                  {/* Root row — accordion chevron toggles the children */}
                  <KostenartRow
                    row={root}
                    isChild={false}
                    isFirst={rootIdx === 0}
                    isLast={rootIdx === rootSiblings.length - 1}
                    onMoveUp={() => handleMove(root, rootSiblings, 'up')}
                    onMoveDown={() => handleMove(root, rootSiblings, 'down')}
                    onToggleActive={() => handleToggleActiveClick(root)}
                    onNameSave={(name) => handleNameSave(root, name)}
                    isMutating={isMutating}
                    childCount={children.length}
                    expanded={expandedRoots.has(root.id)}
                    onToggleExpand={() => toggleRoot(root.id)}
                  />

                  {/* Child rows — only while the root is expanded */}
                  {expandedRoots.has(root.id) && children.map((child, ci) => (
                    <KostenartRow
                      key={child.id}
                      row={child}
                      isChild={true}
                      isFirst={ci === 0}
                      isLast={ci === children.length - 1}
                      onMoveUp={() => handleMove(child, children, 'up')}
                      onMoveDown={() => handleMove(child, children, 'down')}
                      onToggleActive={() => handleToggleActiveClick(child)}
                      onNameSave={(name) => handleNameSave(child, name)}
                      isMutating={isMutating}
                    />
                  ))}
                </div>
              )
            })}
          </div>
        )}
      </section>
    )
  }

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="px-4 sm:px-6 lg:px-10 py-6 max-w-4xl mx-auto">
      <PageHeader
        title={t('page.title')}
        subtitle={t('page.subtitle')}
        actions={
          <div className="flex items-center gap-3">
            {/* Inactive toggle */}
            <Button
              variant={showInactive ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => setShowInactive(v => !v)}
            >
              {showInactive ? t('actions.hideInactive') : t('actions.showInactive')}
            </Button>

            <Button
              variant="primary"
              size="sm"
              icon="add"
              onClick={() => setCreateOpen(true)}
            >
              {t('actions.create')}
            </Button>
          </div>
        }
      />

      {/* Loading / error states */}
      {isLoading && (
        <div className="flex items-center gap-2 py-12 justify-center text-sm text-on-surface-variant">
          <span className="material-symbols-outlined !text-base animate-spin">progress_activity</span>
          {t('loading')}
        </div>
      )}

      {isError && (
        <div className="py-8 text-center text-sm text-rose-600">{t('error')}</div>
      )}

      {/* Content */}
      {!isLoading && !isError && (
        <>
          {renderSection('cost')}
          {renderSection('revenue')}
        </>
      )}

      {/* Create modal */}
      <CreateKostenartModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        roots={roots}
        onSave={handleCreate}
        isSaving={createMutation.isPending}
      />

      {/* Deactivate / activate confirm modal */}
      <ToggleActiveModal
        row={confirmRow}
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirmToggle}
        isMutating={updateMutation.isPending}
      />
    </div>
  )
}
